/**
 * Global Authentication Interceptor
 *
 * Centralizes all 401 error handling to ensure consistent behavior across the application.
 * This utility class provides a wrapper around fetch that automatically handles authentication
 * and session expiration.
 */

export class AuthInterceptor {
  private static onUnauthorized?: () => void;

  /**
   * Register a callback to be executed when a 401 (Unauthorized) error occurs
   *
   * @param handler - Callback function to execute on 401 errors (typically redirects to login)
   * @example
   * ```typescript
   * AuthInterceptor.setUnauthorizedHandler(() => {
   *   setAuthState('login');
   *   setUserEmail(null);
   * });
   * ```
   */
  static setUnauthorizedHandler(handler: () => void): void {
    this.onUnauthorized = handler;
  }

  /**
   * Execute an authenticated fetch request
   *
   * Automatically adds Authorization header with JWT token from localStorage.
   * Handles 401 errors by clearing authentication state and calling the registered handler.
   *
   * @param url - The URL to fetch
   * @param options - Standard fetch options (headers will be merged with auth header)
   * @returns Promise<Response> - The fetch response
   * @throws Error with message 'Authentication required. Please log in again.' on 401
   *
   * @example
   * ```typescript
   * const response = await AuthInterceptor.fetchWithAuth(
   *   'https://api.example.com/data',
   *   { method: 'POST', body: JSON.stringify(data) }
   * );
   * const result = await response.json();
   * ```
   */
  static async fetchWithAuth(
    url: string,
    options: RequestInit = {}
  ): Promise<Response> {
    const token = localStorage.getItem('advisio_access_token');

    // Merge headers with authentication
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Merge existing headers if any
    if (options.headers) {
      const existingHeaders = new Headers(options.headers);
      existingHeaders.forEach((value, key) => {
        headers[key] = value;
      });
    }

    // Add Authorization header if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Execute the fetch request
    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Handle 401 Unauthorized errors
    if (response.status === 401) {
      this.handleUnauthorized();
      throw new Error('Authentication required. Please log in again.');
    }

    return response;
  }

  /**
   * Handle unauthorized access (401 errors)
   *
   * Clears all authentication-related data from localStorage and
   * triggers the registered unauthorized handler callback.
   *
   * @private
   */
  private static handleUnauthorized(): void {
    // Clear all authentication data from localStorage
    localStorage.removeItem('advisio_access_token');
    localStorage.removeItem('advisio_user_email');
    localStorage.removeItem('advisio_auth_state');

    // Call the registered handler (e.g., redirect to login)
    if (this.onUnauthorized) {
      this.onUnauthorized();
    }
  }
}
